import {
  commitNativeGitChanges,
  createNativeGitBranch,
  createNativeGitPullRequest,
  pushNativeGitBranch,
  readNativeGitGenerationContext,
} from '@crowdex/host-kit/renderer/native';
import { Button } from '@crowdex/ui/components/button';
import { Checkbox } from '@crowdex/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import { Textarea } from '@crowdex/ui/components/textarea';
import { toast } from '@crowdex/ui/components/toaster';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import {
  commitWithOptionalBranch,
  createPullRequestWithOptionalBranch,
} from '@crowdex/workspace-protocol/git-action-workflows';
import {
  buildCommitPrompt,
  buildPullRequestPrompt,
  COMMIT_METADATA_JSON_SCHEMA,
  commitMetadataSchema,
  generationScopeKey,
  PULL_REQUEST_METADATA_JSON_SCHEMA,
  pullRequestMetadataSchema,
} from '@crowdex/workspace-protocol/git-generation';
import { GitBranch, GitPullRequest, RefreshCw, Sparkles } from 'lucide-react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { errorMessage } from '../shared/errors';
import { CommitScopeChoice, type ConversationCommitFiles, useCommitScope } from './commit-scope';
import { invalidatePullRequestCockpit } from './pull-request-cockpit-query';

type GitDialog = 'commit' | 'pr' | null;

type GitActionFeedback = {
  success: string;
  failure: string;
};

export type GitMetadataGenerator = (
  prompt: string,
  outputSchema: Readonly<Record<string, unknown>>,
) => Promise<unknown>;

type UseGitActionsParams = {
  workspace: string | null;
  branch: string | null;
  hasChanges: boolean;
  commitsAhead: number;
  currentPullRequest: PullRequestSummary | null;
  pullRequestUrl: string | null;
  onGenerateGitText: GitMetadataGenerator;
  onGitStatusChange: () => Promise<void>;
  /**
   * The files the open conversation's turns changed: commits take them only, unless the user
   * picks the whole folder. None outside a conversation: the folder it is.
   */
  conversationFiles?: ConversationCommitFiles | null;
};

export type GitActions = ReturnType<typeof useGitActions>;

/**
 * Shared git commit / pull-request logic used by both the environment menu and
 * the summary panel. Owns the commit + PR dialog state, AI generation of the
 * commit message and PR body, and the underlying native git actions.
 */
export function useGitActions({
  workspace,
  branch,
  hasChanges,
  commitsAhead,
  currentPullRequest,
  pullRequestUrl,
  onGenerateGitText,
  onGitStatusChange,
  conversationFiles,
}: UseGitActionsParams) {
  const [dialog, setDialog] = useState<GitDialog>(null);
  const [commitMessage, setCommitMessageState] = useState('');
  const [commitBranchName, setCommitBranchName] = useState('');
  const [prTitle, setPrTitleState] = useState('');
  const [prBody, setPrBodyState] = useState('');
  const [prBranchName, setPrBranchName] = useState('');
  const [draft, setDraft] = useState(true);
  const [includeLocalChanges, setIncludeLocalChanges] = useState(true);
  const [createdBranch, setCreatedBranch] = useState<string | null>(null);
  const [commitCompleted, setCommitCompleted] = useState(false);
  const [commitPushed, setCommitPushed] = useState(false);
  const [prCommitCompleted, setPrCommitCompleted] = useState(false);
  const [prPushed, setPrPushed] = useState(false);
  const [working, setWorking] = useState(false);
  const [generating, setGenerating] = useState({ commit: false, pr: false });
  const [error, setError] = useState<string | null>(null);
  const generationScopeRef = useRef('');
  const commitMessageRef = useRef('');
  const prTitleRef = useRef('');
  const prBodyRef = useRef('');
  const commitScope = useCommitScope({ workspace, conversationFiles, active: dialog !== null });

  const shouldPush = !hasChanges && commitsAhead > 0;

  function setCommitMessage(value: string): void {
    commitMessageRef.current = value;
    setCommitMessageState(value);
  }

  function setPrTitle(value: string): void {
    prTitleRef.current = value;
    setPrTitleState(value);
  }

  function setPrBody(value: string): void {
    prBodyRef.current = value;
    setPrBodyState(value);
  }

  async function generate(kind: 'commit' | 'pr', overwrite = false): Promise<void> {
    if (workspace === null || generating[kind]) return;
    setGenerating((current) => ({ ...current, [kind]: true }));
    setError(null);
    try {
      const context = await readNativeGitGenerationContext(workspace, commitScope.paths);
      if (kind === 'commit') {
        const generated = commitMetadataSchema.parse(
          await onGenerateGitText(buildCommitPrompt(context), COMMIT_METADATA_JSON_SCHEMA),
        );
        const message = generated.message;
        if (overwrite || commitMessageRef.current.trim().length === 0) {
          setCommitMessage(message.trim());
        }
      } else {
        const generated = pullRequestMetadataSchema.parse(
          await onGenerateGitText(
            buildPullRequestPrompt({ branch, commitMessage, context }),
            PULL_REQUEST_METADATA_JSON_SCHEMA,
          ),
        );
        const { title, body } = generated;
        if (overwrite || prTitleRef.current.trim().length === 0) setPrTitle(title.trim());
        if (overwrite || prBodyRef.current.trim().length === 0) setPrBody(body.trim());
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setGenerating((current) => ({ ...current, [kind]: false }));
    }
  }

  const resetDrafts = useEffectEvent(() => {
    setCommitMessage('');
    setCommitBranchName('');
    setPrTitle('');
    setPrBody('');
    setPrBranchName('');
    setDraft(true);
    setIncludeLocalChanges(true);
    setError(null);
  });

  useEffect(() => {
    if (workspace === null) return;
    const scope = generationScopeKey(workspace, branch);
    const scopeChanged = generationScopeRef.current !== scope;
    if (scopeChanged) {
      generationScopeRef.current = scope;
      resetDrafts();
    }
  }, [workspace, branch]);

  useEffect(() => {
    if (branch?.trim()) setCreatedBranch(null);
  }, [branch]);

  // The commit message says what the commit takes: written again when its scope changes.
  // Only a change of scope regenerates: the dialog and the changes are read as they are then.
  const regenerateCommitMessage = useEffectEvent(() => {
    if (dialog !== null && hasChanges) void generate('commit', true);
  });
  const scopeRef = useRef(commitScope.scope);
  useEffect(() => {
    if (scopeRef.current === commitScope.scope) return;
    scopeRef.current = commitScope.scope;
    regenerateCommitMessage();
  }, [commitScope.scope]);

  // A commit or pull request made in one folder is not one of the next: its progress resets.
  const progressWorkspaceRef = useRef(workspace);
  useEffect(() => {
    if (progressWorkspaceRef.current === workspace) return;
    progressWorkspaceRef.current = workspace;
    setCreatedBranch(null);
    setCommitCompleted(false);
    setCommitPushed(false);
    setPrCommitCompleted(false);
    setPrPushed(false);
  }, [workspace]);

  async function run(
    action: () => Promise<{ pullRequestUrl: string | null }>,
    feedback: GitActionFeedback,
  ): Promise<void> {
    setWorking(true);
    setError(null);
    try {
      const result = await action();
      if (currentPullRequest !== null) invalidatePullRequestCockpit(currentPullRequest.url);
      setDialog(null);
      if (result.pullRequestUrl !== null) window.open(result.pullRequestUrl, '_blank', 'noopener');
      toast.success(feedback.success);
      try {
        await onGitStatusChange();
      } catch (caught) {
        toast.error('Status refresh failed', errorMessage(caught));
      }
    } catch (caught) {
      const message = errorMessage(caught);
      setError(message);
      toast.error(feedback.failure, message);
    } finally {
      setWorking(false);
    }
  }

  function openCommitOrPush(): void {
    if (workspace === null) return;
    if (shouldPush) {
      void run(() => pushNativeGitBranch(workspace), {
        success: 'Changes pushed',
        failure: 'Couldn’t push changes',
      });
    } else {
      setCommitCompleted(false);
      setCommitPushed(false);
      setDialog('commit');
      if (commitMessageRef.current.trim().length === 0) void generate('commit');
    }
  }

  function openPullRequest(): void {
    const url = currentPullRequest?.url ?? pullRequestUrl;
    if (url !== null && url !== undefined) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    setPrCommitCompleted(false);
    setPrPushed(false);
    setDialog('pr');
    if (hasChanges && includeLocalChanges && commitMessageRef.current.trim().length === 0) {
      void generate('commit');
    }
    if (prTitleRef.current.trim().length === 0 || prBodyRef.current.trim().length === 0) {
      void generate('pr');
    }
  }

  return {
    dialog,
    setDialog,
    commitMessage,
    setCommitMessage,
    commitBranchName,
    setCommitBranchName,
    prTitle,
    setPrTitle,
    prBody,
    setPrBody,
    prBranchName,
    setPrBranchName,
    draft,
    setDraft,
    hasChanges,
    includeLocalChanges,
    setIncludeLocalChanges,
    createdBranch,
    setCreatedBranch,
    commitCompleted,
    setCommitCompleted,
    commitPushed,
    setCommitPushed,
    prCommitCompleted,
    setPrCommitCompleted,
    prPushed,
    setPrPushed,
    working,
    generating,
    error,
    commitScope,
    shouldPush,
    generate,
    run,
    openCommitOrPush,
    openPullRequest,
  };
}

/** Renders the commit and pull-request dialogs driven by {@link useGitActions}. */
export function GitActionDialogs({
  actions,
  branch,
  existingBranches = [],
  workspace,
}: {
  actions: GitActions;
  branch: string | null;
  existingBranches?: readonly string[];
  workspace: string | null;
}) {
  const {
    dialog,
    setDialog,
    commitMessage,
    setCommitMessage,
    commitBranchName,
    setCommitBranchName,
    prTitle,
    setPrTitle,
    prBody,
    setPrBody,
    prBranchName,
    setPrBranchName,
    draft,
    setDraft,
    hasChanges,
    includeLocalChanges,
    setIncludeLocalChanges,
    createdBranch,
    setCreatedBranch,
    commitCompleted,
    setCommitCompleted,
    commitPushed,
    setCommitPushed,
    prCommitCompleted,
    setPrCommitCompleted,
    prPushed,
    setPrPushed,
    working,
    generating,
    error,
    commitScope,
    generate,
    run,
  } = actions;
  // The commit takes the scope's files: the conversation's, or (no paths) the whole folder.
  const commitScoped = (input: { workspaceRoot: string; message: string; push: boolean }) =>
    commitNativeGitChanges({
      ...input,
      ...(commitScope.paths === undefined ? {} : { paths: commitScope.paths }),
    });
  const nothingInScope = commitScope.preview !== null && commitScope.preview.files.length === 0;
  const effectiveBranch = branch?.trim() || createdBranch;
  const hasCurrentBranch = effectiveBranch !== null && effectiveBranch.length > 0;
  const commitBranchExists = existingBranches.includes(commitBranchName.trim());
  const pullRequestBranchExists = existingBranches.includes(prBranchName.trim());

  async function commitFromDialog(push: boolean) {
    if (workspace === null) return { pullRequestUrl: null };
    return commitWithOptionalBranch(
      {
        workspace,
        currentBranch: effectiveBranch,
        newBranch: commitBranchName,
        message: commitMessage,
        push,
        commitCompleted,
        pushCompleted: commitPushed,
      },
      {
        createBranch: createNativeGitBranch,
        onBranchCreated: setCreatedBranch,
        commit: commitScoped,
        onCommitted: () => setCommitCompleted(true),
        push: pushNativeGitBranch,
        onPushed: () => setCommitPushed(true),
      },
    );
  }

  async function createPullRequestFromDialog() {
    if (workspace === null) return { pullRequestUrl: null };
    return createPullRequestWithOptionalBranch(
      {
        workspace,
        currentBranch: effectiveBranch,
        newBranch: prBranchName,
        hasChanges,
        includeLocalChanges,
        commitMessage,
        title: prTitle,
        body: prBody,
        draft,
        commitCompleted: prCommitCompleted,
        pushCompleted: prPushed,
      },
      {
        createBranch: createNativeGitBranch,
        onBranchCreated: setCreatedBranch,
        commit: commitScoped,
        onCommitted: () => setPrCommitCompleted(true),
        push: pushNativeGitBranch,
        onPushed: () => setPrPushed(true),
        createPullRequest: createNativeGitPullRequest,
      },
    );
  }

  return (
    <>
      <Dialog
        open={dialog === 'commit'}
        onOpenChange={(open) => !working && setDialog(open ? 'commit' : null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Commit or push</DialogTitle>
            <DialogDescription>
              {hasCurrentBranch
                ? `Commit to ${effectiveBranch}.`
                : 'Create a branch, then commit the changes.'}
            </DialogDescription>
          </DialogHeader>
          <CommitScopeChoice state={commitScope} />
          {!hasCurrentBranch && (
            <label className="grid gap-2 text-sm" htmlFor="commit-branch-name">
              <span>Branch name</span>
              <Input
                autoFocus
                id="commit-branch-name"
                onChange={(event) => setCommitBranchName(event.target.value)}
                placeholder="feature/my-change"
                value={commitBranchName}
              />
              {commitBranchExists && (
                <span className="text-destructive text-xs">Branch exists</span>
              )}
            </label>
          )}
          <label className="grid gap-2 text-sm" htmlFor="commit-message">
            <span className="flex items-center justify-between">
              Commit message
              <Button
                disabled={generating.commit}
                onClick={() => void generate('commit', true)}
                size="sm"
                type="button"
                variant="ghost"
              >
                {generating.commit ? (
                  <RefreshCw className="size-3.5 animate-spin" />
                ) : (
                  <Sparkles className="size-3.5" />
                )}
                {generating.commit ? 'Generating…' : 'Regenerate'}
              </Button>
            </span>
            <Textarea
              autoFocus={hasCurrentBranch}
              className="min-h-24 resize-y"
              id="commit-message"
              onChange={(event) => setCommitMessage(event.target.value)}
              placeholder="Summarize your changes"
              value={commitMessage}
            />
          </label>
          {error !== null && <p className="text-destructive text-sm">{error}</p>}
          <DialogFooter>
            <Button disabled={working} onClick={() => setDialog(null)} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={
                working ||
                nothingInScope ||
                commitMessage.trim().length === 0 ||
                workspace === null ||
                (!hasCurrentBranch && commitBranchName.trim().length === 0) ||
                (!hasCurrentBranch && commitBranchExists)
              }
              onClick={() =>
                workspace !== null &&
                void run(() => commitFromDialog(true), {
                  success: 'Changes committed and pushed',
                  failure: 'Couldn’t commit and push changes',
                })
              }
            >
              {working ? 'Working…' : 'Commit and push'}
            </Button>
            <Button
              disabled={
                working ||
                nothingInScope ||
                commitMessage.trim().length === 0 ||
                workspace === null ||
                (!hasCurrentBranch && commitBranchName.trim().length === 0) ||
                (!hasCurrentBranch && commitBranchExists)
              }
              onClick={() =>
                workspace !== null &&
                void run(() => commitFromDialog(false), {
                  success: 'Changes committed',
                  failure: 'Couldn’t commit changes',
                })
              }
              variant="secondary"
            >
              Commit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialog === 'pr'}
        onOpenChange={(open) => !working && setDialog(open ? 'pr' : null)}
      >
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl sm:p-0">
          <DialogHeader className="shrink-0 gap-0 border-hairline border-b bg-muted/25 p-6 pr-14">
            <div className="flex items-start gap-3.5">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-hairline-strong bg-background shadow-sm">
                <GitPullRequest className="size-5 text-foreground/80" />
              </div>
              <div className="min-w-0 pt-0.5">
                <DialogTitle className="text-lg">Create pull request</DialogTitle>
                <DialogDescription className="mt-1">
                  Review the generated details before publishing your branch.
                </DialogDescription>
              </div>
              <Button
                className="ml-auto shrink-0"
                disabled={generating.pr || generating.commit}
                onClick={() => {
                  if (hasChanges && includeLocalChanges && commitMessage.trim().length === 0) {
                    void generate('commit');
                  }
                  void generate('pr', true);
                }}
                size="sm"
                type="button"
                variant="outline"
              >
                {generating.pr ? (
                  <RefreshCw className="size-3.5 animate-spin" />
                ) : (
                  <Sparkles className="size-3.5" />
                )}
                {generating.pr ? 'Generating…' : 'Regenerate'}
              </Button>
            </div>
          </DialogHeader>
          <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto overscroll-contain p-6">
            {hasCurrentBranch ? (
              <div className="flex items-center gap-2 rounded-lg border border-hairline bg-background/55 px-3 py-2 text-muted-foreground text-xs">
                <GitBranch className="size-3.5 shrink-0" />
                <span className="min-w-0 truncate font-medium text-foreground/80">
                  {effectiveBranch}
                </span>
                <span className="ml-auto shrink-0">will be pushed automatically</span>
              </div>
            ) : (
              <label className="grid gap-2 text-sm" htmlFor="pull-request-branch-name">
                <span className="font-medium text-foreground/90">Branch name</span>
                <Input
                  className="h-11 rounded-lg px-3.5 placeholder:text-muted-foreground/60"
                  id="pull-request-branch-name"
                  onChange={(event) => setPrBranchName(event.target.value)}
                  placeholder="feature/my-change"
                  value={prBranchName}
                />
                {pullRequestBranchExists && (
                  <span className="text-destructive text-xs">Branch exists</span>
                )}
                <span className="text-muted-foreground text-xs">
                  A new branch will be created from this worktree.
                </span>
              </label>
            )}

            <label className="grid gap-2 text-sm" htmlFor="pull-request-title">
              <span className="font-medium text-foreground/90">Title</span>
              <Input
                autoFocus
                className="h-11 rounded-lg px-3.5 placeholder:text-muted-foreground/60"
                id="pull-request-title"
                onChange={(event) => setPrTitle(event.target.value)}
                placeholder="Summarize the change"
                value={prTitle}
              />
            </label>
            <label className="grid gap-2 text-sm" htmlFor="pull-request-description">
              <span className="flex items-center justify-between gap-3">
                <span className="font-medium text-foreground/90">Description</span>
                <span className="text-muted-foreground text-xs">Markdown supported</span>
              </span>
              <Textarea
                className="min-h-36 resize-y rounded-lg px-3.5 py-3 leading-5 placeholder:text-muted-foreground/60"
                id="pull-request-description"
                onChange={(event) => setPrBody(event.target.value)}
                placeholder="What changed, and why?"
                value={prBody}
              />
            </label>

            <label
              className="flex cursor-pointer items-start gap-3 rounded-xl border border-hairline bg-muted/20 p-3.5 transition-colors hover:bg-muted/35"
              htmlFor="pull-request-draft"
            >
              <Checkbox
                checked={draft}
                className="mt-0.5"
                id="pull-request-draft"
                onCheckedChange={(checked) => setDraft(checked === true)}
              />
              <span className="grid gap-0.5">
                <span className="font-medium text-sm">Create as draft</span>
                <span className="text-muted-foreground text-xs leading-5">
                  Open the pull request for early feedback before it is ready to merge.
                </span>
              </span>
            </label>
            {hasChanges && (
              <label
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-hairline bg-muted/20 p-3.5 transition-colors hover:bg-muted/35"
                htmlFor="pull-request-include-local-changes"
              >
                <Checkbox
                  checked={includeLocalChanges}
                  className="mt-0.5"
                  id="pull-request-include-local-changes"
                  onCheckedChange={(checked) => {
                    const include = checked === true;
                    setIncludeLocalChanges(include);
                    if (include && commitMessage.trim().length === 0) void generate('commit');
                  }}
                />
                <span className="grid gap-0.5">
                  <span className="font-medium text-sm">Commit and push local changes</span>
                  <span className="text-muted-foreground text-xs leading-5">
                    Include the current worktree changes in this pull request.
                  </span>
                </span>
              </label>
            )}
            {hasChanges && includeLocalChanges && <CommitScopeChoice state={commitScope} />}
            {error !== null && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive text-sm">
                {error}
              </p>
            )}
          </div>
          <DialogFooter className="m-0 shrink-0 rounded-none border-hairline border-t px-6 py-4 sm:m-0">
            <Button disabled={working} onClick={() => setDialog(null)} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={
                working ||
                generating.pr ||
                prTitle.trim().length === 0 ||
                prBody.trim().length === 0 ||
                workspace === null ||
                (hasChanges &&
                  includeLocalChanges &&
                  (generating.commit || commitMessage.trim().length === 0)) ||
                (!hasCurrentBranch && prBranchName.trim().length === 0) ||
                (!hasCurrentBranch && pullRequestBranchExists)
              }
              onClick={() =>
                workspace !== null &&
                void run(createPullRequestFromDialog, {
                  success: draft ? 'Draft pull request published' : 'Pull request published',
                  failure: 'Couldn’t publish pull request',
                })
              }
            >
              <GitPullRequest className="size-4" />
              {working ? 'Creating…' : draft ? 'Create draft PR' : 'Create PR'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
